import { authService } from './auth.service';

class ApiService {
  private api = authService.getApiInstance();

  /**
   * 🛡️ Verificar autenticación antes de cada llamada
   */
  private ensureAuthenticated(): void {
    if (!authService.isAuthenticated()) {
      throw new Error('Usuario no autenticado');
    }
  }

  // ================================
  // 🌐 MÉTODOS HTTP GENÉRICOS
  // ================================

  /**
   * GET request genérico
   */
  async get<T = any>(url: string, config?: any): Promise<{ data: T }> {
    this.ensureAuthenticated();
    return await this.api.get(url, config);
  }

  /**
   * POST request genérico
   */
  async post<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    this.ensureAuthenticated();
    return await this.api.post(url, data, config);
  }

  /**
   * PUT request genérico
   */
  async put<T = any>(url: string, data?: any, config?: any): Promise<{ data: T }> {
    this.ensureAuthenticated();
    return await this.api.put(url, data, config);
  }

  /**
   * DELETE request genérico
   */
  async delete<T = any>(url: string, config?: any): Promise<{ data: T }> {
    this.ensureAuthenticated();
    return await this.api.delete(url, config);
  }

  // ================================
  // 👤 MÓDULOS DE USUARIO
  // ================================

  /**
   * Obtener módulos del usuario autenticado
   */
  async getUserModules(userId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/user-module-permissions/${userId}/modules`);
    return response.data;
  }

  // ================================
  // 🏆 EVENTOS
  // ================================

  /**
   * Obtener todos los eventos
   */
  async getEvents(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/events');
    return response.data;
  }

  /**
   * Crear un nuevo evento
   */
  async createEvent(eventData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/events', eventData);
    return response.data;
  }

  /**
   * Actualizar un evento
   */
  async updateEvent(eventId: string, eventData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/events/${eventId}`, eventData);
    return response.data;
  }

  /**
   * Eliminar un evento
   */
  async deleteEvent(eventId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/events/${eventId}`);
  }

  /**
   * Subir logo de un evento (multipart/form-data)
   */
  async uploadEventLogo(eventId: string, file: File): Promise<{ message: string; logoUrl: string }> {
    this.ensureAuthenticated();
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.api.post(`/events/${eventId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Eliminar logo de un evento
   */
  async deleteEventLogo(eventId: string): Promise<{ message: string }> {
    this.ensureAuthenticated();
    const response = await this.api.delete(`/events/${eventId}/logo`);
    return response.data;
  }

  // ================================
  // 🏟️ ESTADIOS
  // ================================

  /**
   * Obtener todos los estadios
   */
  async getStadiums(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/stadiums');
    return response.data;
  }

  /**
   * Crear un estadio
   */
  async createStadium(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/stadiums', data);
    return response.data;
  }

  /**
   * Actualizar un estadio
   */
  async updateStadium(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/stadiums/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar un estadio
   */
  async deleteStadium(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/stadiums/${id}`);
  }

  // ================================
  // 📚 CATÁLOGOS
  // ================================

  /**
   * Obtener todos los géneros
   */
  async getGenders(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/genders');
    return response.data;
  }

  /**
   * Crear un género
   */
  async createGender(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/genders', data);
    return response.data;
  }

  /**
   * Actualizar un género
   */
  async updateGender(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/genders/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar un género
   */
  async deleteGender(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/genders/${id}`);
  }

  /**
   * Obtener todas las categorías
   */
  async getCategories(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/categories');
    return response.data;
  }

  /**
   * Crear una categoría
   */
  async createCategory(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/categories', data);
    return response.data;
  }

  /**
   * Actualizar una categoría
   */
  async updateCategory(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/categories/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar una categoría
   */
  async deleteCategory(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/categories/${id}`);
  }

  // ================================
  // 🌍 UBIGEO
  // ================================

  /**
   * Obtener todos los países
   */
  async getCountries(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/ubigeo/countries');
    return response.data;
  }

  /**
   * Crear un país
   */
  async createCountry(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/ubigeo/countries', data);
    return response.data;
  }

  /**
   * Actualizar un país
   */
  async updateCountry(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/ubigeo/countries/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar un país
   */
  async deleteCountry(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/ubigeo/countries/${id}`);
  }

  /**
   * Obtener departamentos (opcionalmente por país)
   */
  async getDepartments(countryId?: string): Promise<any[]> {
    this.ensureAuthenticated();
    const params = countryId ? { countryId } : {};
    const response = await this.api.get('/ubigeo/departments', { params });
    return response.data;
  }

  /**
   * Crear un departamento
   */
  async createDepartment(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/ubigeo/departments', data);
    return response.data;
  }

  /**
   * Actualizar un departamento
   */
  async updateDepartment(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/ubigeo/departments/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar un departamento
   */
  async deleteDepartment(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/ubigeo/departments/${id}`);
  }

  /**
   * Obtener provincias (opcionalmente por departamento)
   */
  async getProvinces(departmentId?: string): Promise<any[]> {
    this.ensureAuthenticated();
    const params = departmentId ? { departmentId } : {};
    const response = await this.api.get('/ubigeo/provinces', { params });
    return response.data;
  }

  /**
   * Crear una provincia
   */
  async createProvince(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/ubigeo/provinces', data);
    return response.data;
  }

  /**
   * Actualizar una provincia
   */
  async updateProvince(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/ubigeo/provinces/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar una provincia
   */
  async deleteProvince(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/ubigeo/provinces/${id}`);
  }

  /**
   * Obtener distritos (opcionalmente por provincia)
   */
  async getDistricts(provinceId?: string): Promise<any[]> {
    this.ensureAuthenticated();
    const params = provinceId ? { provinceId } : {};
    const response = await this.api.get('/ubigeo/districts', { params });
    return response.data;
  }

  /**
   * Crear un distrito
   */
  async createDistrict(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/ubigeo/districts', data);
    return response.data;
  }

  /**
   * Actualizar un distrito
   */
  async updateDistrict(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/ubigeo/districts/${id}`, data);
    return response.data;
  }

  /**
   * Eliminar un distrito
   */
  async deleteDistrict(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/ubigeo/districts/${id}`);
  }

  /**
   * Obtener todas las jornadas
   */
  async getJornadas(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/jornadas');
    return response.data;
  }

  /**
   * Obtener jornadas relacionadas a un evento
   */
  async getEventJornadas(eventId: string): Promise<any[]> {
    this.ensureAuthenticated();
    console.log('🔍 API: Getting event jornadas for eventId:', eventId);
    
    try {
      // Endpoint documentado: /event-jornadas/event/:eventId
      const response = await this.api.get(`/event-jornadas/event/${eventId}`);
      console.log('✅ API: Event jornadas retrieved successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Error getting event jornadas:', {
        eventId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Fallback legacy: obtener todas y filtrar
      try {
        console.log('🔄 API: Trying to get all event-jornadas and filter by eventId');
        const allResponse = await this.api.get(`/event-jornadas`);
        const allRelations = allResponse.data;
        
        // Filtrar por eventId
        const eventRelations = Array.isArray(allRelations) 
          ? allRelations.filter((relation: any) => relation.eventId === eventId)
          : [];
        
        console.log('✅ API: Event jornadas filtered successfully:', eventRelations);
        return eventRelations;
      } catch (fallbackError: any) {
        console.error('❌ API: Fallback also failed:', fallbackError);
        throw error; // Throw original error
      }
    }
  }

  /**
   * Relacionar jornada con evento
   */
  async linkJornadaToEvent(eventId: string, jornadaId: string, date?: string): Promise<any> {
    this.ensureAuthenticated();
    
    const payload: any = {
      eventId,
      jornadaId
    };
    
    if (date) {
      payload.date = date;
    }
    
    console.log('🔗 API: Linking jornada to event with payload:', payload);
    
    try {
      const response = await this.api.post(`/event-jornadas`, payload);
      console.log('✅ API: Jornada linked successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API: Error linking jornada to event:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        payload
      });
      throw error;
    }
  }

  /**
   * Desrelacionar jornada de evento
   */
  async unlinkJornadaFromEvent(relationId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/event-jornadas/${relationId}`);
  }

  // ================================
  // 🧪 PRUEBAS (TESTS)
  // ================================

  /**
   * Obtener pruebas de un evento específico
   */
  async getEventTests(eventId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/event-tests/event/${eventId}`);
    return response.data;
  }

  /**
   * Obtener todas las pruebas
   */
  async getTests(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/tests');
    return response.data;
  }

  async getCombinedTestsCatalog(query = ''): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/tests/combined-catalog', {
      params: { q: query || undefined },
    });
    return response.data?.data || response.data || [];
  }

  /**
   * Crear una nueva prueba
   */
  async createTest(testData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/tests', testData);
    return response.data;
  }

  /**
   * Actualizar una prueba
   */
  async updateTest(testId: string, testData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/tests/${testId}`, testData);
    return response.data;
  }

  /**
   * Eliminar una prueba
   */
  async deleteTest(testId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/tests/${testId}`);
  }

  /**
   * Crear un event-test (prueba de evento)
   */
  async createEventTest(eventTestData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/event-tests', eventTestData);
    return response.data;
  }

  /**
   * Actualizar un event-test
   */
  async updateEventTest(eventTestId: string, eventTestData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/event-tests/${eventTestId}`, eventTestData);
    return response.data;
  }

  /**
   * Eliminar un event-test
   */
  async deleteEventTest(eventTestId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/event-tests/${eventTestId}`);
  }

  /**
   * PATCH nombre global de una prueba (afecta todos los eventos)
   */
  async patchTestName(testId: string, data: { official_name_spanish: string; common_name_spanish?: string }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.patch(`/tests/${testId}/name`, data);
    return response.data;
  }

  /**
   * PATCH nombre personalizado de un event-test (solo este evento)
   */
  async patchEventTestCustomName(eventTestId: string, data: { customName: string | null }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.patch(`/event-tests/${eventTestId}/custom-name`, data);
    return response.data;
  }

  async setEventTestIsFinal(eventTestId: string, isFinal: boolean): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.patch(`/event-tests/${eventTestId}/is-final`, { isFinal });
    return response.data;
  }

  // ================================
  // 📊 SERIES
  // ================================

  /**
   * Obtener series de una prueba
   */
  async getTestSeries(testId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/tests/${testId}/series`);
    return response.data;
  }

  /**
   * Obtener series de un event-test específico
   */
  async getEventTestSeries(eventTestId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/series/event-test/${eventTestId}`);
    return response.data;
  }

  /**
   * Crear una nueva serie
   */
  async createSeries(seriesData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/series', seriesData);
    return response.data;
  }

  /**
   * Actualizar una serie
   */
  async updateSeries(seriesId: string, seriesData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/series/${seriesId}`, seriesData);
    return response.data;
  }

  /**
   * Eliminar una serie
   */
  async deleteSeries(seriesId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/series/${seriesId}`);
  }

  /**
   * Reordenar series
   */
  async reorderSeries(seriesOrder: { id: string; order: number }[]): Promise<void> {
    this.ensureAuthenticated();
    await this.api.put('/series/reorder', { seriesOrder });
  }

  // ================================
  // 📋 DATOS LIF
  // ================================

  /**
   * Obtener datos LIF de una serie
   */
  async getLifData(seriesId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/lif-data/series/${seriesId}`);
    return response.data;
  }

  /**
   * Obtener datos LIF por ID
   */
  async getLifDataById(lifDataId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/lif-data/lifdata/${lifDataId}`);
    return response.data;
  }

  /**
   * Crear datos LIF
   */
  async createLifData(lifData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/lif-data/lifdata', lifData);
    return response.data;
  }

  /**
   * Actualizar datos LIF
   */
  async updateLifData(lifDataId: string, lifData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/lif-data/lifdata/${lifDataId}`, lifData);
    return response.data;
  }

  /**
   * Subir archivo LIF
   */
  async uploadLifFile(seriesId: string, file: File, additionalData?: any): Promise<any> {
    this.ensureAuthenticated();
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Agregar datos adicionales si se proporcionan
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const response = await this.api.post(`/lif-data/upload/${seriesId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  }

  /**
   * Eliminar datos LIF de una serie
   */
  async deleteLifData(seriesId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/lif-data/series/${seriesId}`);
  }

  /**
   * Eliminar datos LIF por ID específico
   */
  async deleteLifDataById(lifDataId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/lif-data/lifdata/${lifDataId}`);
  }

  // ================================
  // 📝 DATOS MANUALES
  // ================================

  /**
   * Crear datos manuales para una serie
   */
  async createManualData(seriesId: string, manualData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/manual-data/series/${seriesId}`, manualData);
    return response.data;
  }

  /**
   * Actualizar datos manuales
   */
  async updateManualData(manualDataId: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/manual-data/${manualDataId}`, data);
    return response.data;
  }

  /**
   * Eliminar datos manuales
   */
  async deleteManualData(manualDataId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/manual-data/${manualDataId}`);
  }

  // ================================
  // 📈 RESULTADOS
  // ================================

  /**
   * Obtener resultados de una serie
   */
  async getSeriesResults(seriesId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/series/${seriesId}/results`);
    return response.data;
  }

  /**
   * Actualizar resultado específico
   */
  async updateResult(resultId: string, resultData: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/results/${resultId}`, resultData);
    return response.data;
  }

  // ================================
  // 🔍 BÚSQUEDAS Y FILTROS
  // ================================

  /**
   * Buscar eventos con filtros
   */
  async searchEvents(filters: any): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/events/search', { params: filters });
    return response.data;
  }

  /**
   * Buscar pruebas con filtros
   */
  async searchTests(filters: any): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/tests/search', { params: filters });
    return response.data;
  }

  // ================================
  // 📊 REPORTES Y EXPORTACIÓN
  // ================================

  /**
   * Exportar resultados de una prueba
   */
  async exportTestResults(testId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<Blob> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/tests/${testId}/export`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  /**
   * Generar reporte de evento
   */
  async generateEventReport(eventId: string): Promise<Blob> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/events/${eventId}/report`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ================================
  // 🔧 UTILIDADES
  // ================================

  /**
   * Verificar conectividad con el servidor
   */
  async ping(): Promise<boolean> {
    try {
      // Usar el endpoint de login sin autenticación para verificar conectividad
      const response = await fetch(`${process.env.VUE_APP_API_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Cuerpo vacío para probar conectividad
      });
      
      // Si el servidor responde (aunque sea con error), significa que está conectado
      return true;
    } catch (error: any) {
      console.warn('Server connectivity check failed:', error);
      return false;
    }
  }

  /**
   * Obtener información del servidor
   */
  async getServerInfo(): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get('/server/info');
    return response.data;
  }

  // ================================
  // 📄 GESTIÓN DE ARCHIVOS
  // ================================

  /**
   * Subir archivo genérico
   */
  async uploadFile(endpoint: string, file: File, additionalData?: any): Promise<any> {
    this.ensureAuthenticated();
    
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  }

  /**
   * Descargar archivo
   */
  async downloadFile(endpoint: string, filename?: string): Promise<void> {
    this.ensureAuthenticated();
    
    const response = await this.api.get(endpoint, {
      responseType: 'blob'
    });

    // Crear URL temporal para descarga
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || 'download');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // ================================
  // 📅 CALENDARIO DE ACTIVIDADES
  // ================================

  /**
   * 🔓 PÚBLICAS - Años disponibles en el calendario
   */
  async getCalendarYears(): Promise<number[]> {
    const response = await this.api.get('/public/calendar/years');
    return response.data?.data || response.data || [];
  }

  /**
   * 🔓 PÚBLICAS - Obtener actividades por mes
   */
  async getCalendarActivitiesByMonth(year: number, month: number): Promise<any[]> {
    // No requiere autenticación - API pública
    const response = await this.api.get(`/public/calendar/${year}/${month}`);
    return response.data.data || response.data;
  }

  /**
   * 🔓 PÚBLICAS - Obtener actividades por rango de fechas
   */
  async getCalendarActivitiesByRange(startDate: string, endDate: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/calendar-activities/range`, {
      params: { startDate, endDate }
    });
    return response.data.data || response.data;
  }

  /**
   * 🔓 PÚBLICAS - Obtener detalle de una actividad
   */
  async getPublicCalendarActivity(activityId: string): Promise<any> {
    // No requiere autenticación - API pública
    const response = await this.api.get(`/public/calendar/${activityId}`);
    return response.data.data || response.data;
  }

  /**
   * 🔒 ADMIN - Listar todas las actividades, filtradas por año opcional
   */
  async getCalendarActivities(year?: number | null): Promise<any[]> {
    this.ensureAuthenticated();
    const params: any = {};
    if (year) params.year = year;
    const response = await this.api.get('/calendar-activities', { params });
    return response.data.data || response.data;
  }

  /**
   * 🔒 ADMIN - Obtener detalle de actividad (Admin)
   */
  async getCalendarActivity(activityId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/calendar-activities/${activityId}`);
    return response.data.data || response.data;
  }

  /**
   * 🔒 ADMIN - Crear nueva actividad
   */
  async createCalendarActivity(activityData: {
    title: string;
    description: string;
    dateStart?: string;
    dateEnd?: string;
    startDate?: string;
    endDate?: string;
    location: string;
    type: string;
    status?: string;
    isPublic?: boolean;
    position?: number;
    eventId?: string | null;
    color?: string | null;
  }): Promise<any> {
    this.ensureAuthenticated();
    const payload = {
      ...activityData,
      dateStart: activityData.dateStart || activityData.startDate,
      dateEnd: activityData.dateEnd || activityData.endDate,
      // compatibilidad temporal
      startDate: activityData.startDate || activityData.dateStart,
      endDate: activityData.endDate || activityData.dateEnd,
    };
    const response = await this.api.post('/calendar-activities', payload);
    return response.data.data || response.data;
  }

  /**
   * 🔒 ADMIN - Actualizar actividad
   */
  async updateCalendarActivity(activityId: string, activityData: Partial<{
    title: string;
    description: string;
    dateStart: string;
    dateEnd: string;
    startDate: string;
    endDate: string;
    location: string;
    type: string;
    status: string;
    isPublic: boolean;
    position: number;
    eventId: string | null;
    color: string | null;
  }>): Promise<any> {
    this.ensureAuthenticated();
    const payload = {
      ...activityData,
      dateStart: (activityData as any).dateStart || (activityData as any).startDate,
      dateEnd: (activityData as any).dateEnd || (activityData as any).endDate,
      // compatibilidad temporal
      startDate: (activityData as any).startDate || (activityData as any).dateStart,
      endDate: (activityData as any).endDate || (activityData as any).dateEnd,
    };
    const response = await this.api.put(`/calendar-activities/${activityId}`, payload);
    return response.data.data || response.data;
  }

  /**
   * 🔒 ADMIN - Eliminar actividad
   */
  async deleteCalendarActivity(activityId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/calendar-activities/${activityId}`);
  }

  /**
   * 🔓 PÚBLICAS - Descargar PDF del calendario
   */
  async downloadCalendarPdf(range: string = 'upcoming', year?: number | null): Promise<{ blob: Blob; fileName?: string }> {
    const params: any = {};
    if (year) params.year = year;
    const response = await this.api.get(`/calendar-activities/pdf/${range}`, {
      responseType: 'blob',
      params,
    });

    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
    const fileName = this.parseFileNameFromDisposition(disposition) || `calendario-${range}${year ? `-${year}` : ''}.pdf`;

    return {
      blob: response.data,
      fileName,
    };
  }

  // ================================
  // 🐆 LYNX IMPORT (.evt)
  // ================================

  /**
   * Preview del archivo Lynx (.evt) para un evento
   */
  async previewLynxImport(file: File, eventId: string): Promise<any> {
    this.ensureAuthenticated();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);

    const response = await this.api.post('/lynx-import/preview', formData, {
      timeout: 120000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Confirmar importación Lynx luego de la revisión en frontend
   */
  async confirmLynxImport(payload: {
    eventId: string;
    jornadaId: string;
    testGroups: Array<{
      testId: string | null;
      newTestName: string;
      newTestType: string;
      newTestInputFormat: string;
      newTestMeasuresWind: boolean;
      lynxTestCode: string;
      genderId: string | null;
      categoryId: string | null;
      series: Array<{
        seriesNumber: number;
        athletes: Array<{
          bibNumber: string;
          lane: string;
          fullName: string;
          club: string;
        }>;
      }>;
    }>;
  }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/lynx-import/confirm', payload, {
      timeout: 180000,
    });
    return response.data;
  }

  // ================================
  // 🏅 RECORDS NACIONALES
  // ================================

  /**
   * Subir archivo de Records Nacionales (Excel)
   */
  async uploadNationalRecords(
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    const config: any = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onUploadProgress) {
      config.onUploadProgress = onUploadProgress;
    }

    const response = await this.api.post('/national-records/upload', formData, config);
    return response.data;
  }

  /**
   * Obtener categorías disponibles de récords nacionales (retorna strings)
   */
  async getNationalRecordCategories(): Promise<string[]> {
    const response = await this.api.get('/national-records/categories');
    const data = response.data;
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      return data.map((item: any) => item.category);
    }
    return data;
  }

  /**
   * Obtener categorías con su orden actual (para el panel de orden de admin)
   */
  async getNationalRecordCategoriesWithOrder(): Promise<{ category: string; categoryOrder: number }[]> {
    const response = await this.api.get('/national-records/categories');
    const data = response.data;
    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
      return data.map((cat: string) => ({ category: cat, categoryOrder: 0 }));
    }
    return data;
  }

  /**
   * Guardar nuevo orden de categorías (PATCH, solo admin)
   */
  async updateNationalRecordCategoriesOrder(
    data: { category: string; order: number }[]
  ): Promise<any> {
    const response = await this.api.patch('/national-records/categories/order', data);
    return response.data;
  }

  /**
   * Obtener récords por categoría
   */
  async getNationalRecordsByCategory(category: string): Promise<any[]> {
    const encodedCategory = encodeURIComponent(category);
    const response = await this.api.get(`/national-records/category/${encodedCategory}`);
    return response.data;
  }

  /**
   * Buscar récords por atleta
   */
  async searchNationalRecordsByAthlete(name: string): Promise<any[]> {
    const response = await this.api.get('/national-records/search/athlete', {
      params: { name }
    });
    return response.data;
  }

  /**
   * Buscar récords por evento
   */
  async searchNationalRecordsByEvent(event: string): Promise<any[]> {
    const response = await this.api.get('/national-records/search/event', {
      params: { event }
    });
    return response.data;
  }

  /**
   * Obtener todos los récords nacionales
   */
  async getAllNationalRecords(): Promise<any[]> {
    const response = await this.api.get('/national-records');
    return response.data;
  }

  /**
   * Obtener un récord nacional por ID
   */
  async getNationalRecordById(id: number | string): Promise<any> {
    const response = await this.api.get(`/national-records/${id}`);
    return response.data;
  }

  /**
   * Crear un récord nacional individual
   */
  async createNationalRecord(payload: {
    category: string;
    event: string;
    record?: string | null;
    wind?: string | null;
    athlete?: string | null;
    place?: string | null;
    recordDate?: string | null;
    coach?: string | null;
    rowOrder?: number;
  }): Promise<any> {
    const response = await this.api.post('/national-records', payload);
    return response.data;
  }

  /**
   * Actualizar un récord nacional individual
   */
  async updateNationalRecord(
    id: number | string,
    payload: Partial<{
      category: string;
      event: string;
      record: string | null;
      wind: string | null;
      athlete: string | null;
      place: string | null;
      recordDate: string | null;
      coach: string | null;
      rowOrder: number;
    }>
  ): Promise<any> {
    const response = await this.api.put(`/national-records/${id}`, payload);
    return response.data;
  }

  /**
   * Eliminar un récord nacional individual
   */
  async deleteNationalRecord(id: number | string): Promise<any> {
    const response = await this.api.delete(`/national-records/${id}`);
    return response.data;
  }

  /**
   * Reordenar récords de una categoría por drag & drop
   */
  async reorderNationalRecords(category: string, orderedIds: (number | string)[]): Promise<any> {
    const response = await this.api.patch('/national-records/records/reorder', {
      category,
      orderedIds,
    });
    return response.data;
  }

  /**
   * Obtener estadísticas generales de récords nacionales
   */
  async getNationalRecordsStatistics(): Promise<any> {
    const response = await this.api.get('/national-records/statistics');
    return response.data;
  }

  // ================================
  // 🎯 MARCAS MÍNIMAS
  // ================================

  async getMinimumMarksSets(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/minimum-marks/sets');
    return response.data?.data || response.data || [];
  }

  async getMinimumMarksSetById(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/minimum-marks/sets/${id}`);
    return response.data?.data || response.data;
  }

  async getMinimumMarksCatalog(query = ''): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/minimum-marks/catalog', {
      params: { q: query || undefined },
    });
    return response.data?.data || response.data || [];
  }

  async createMinimumMarksSet(payload: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/minimum-marks/sets', payload);
    return response.data?.data || response.data;
  }

  async updateMinimumMarksSet(id: string, payload: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/minimum-marks/sets/${id}`, payload);
    return response.data?.data || response.data;
  }

  async deleteMinimumMarksSet(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/minimum-marks/sets/${id}`);
  }

  async addMinimumMarks(setId: string, marks: any[]): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/minimum-marks/sets/${setId}/marks`, marks);
    return response.data?.data || response.data;
  }

  async updateMinimumMark(id: string, payload: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/minimum-marks/marks/${id}`, payload);
    return response.data?.data || response.data;
  }

  async deleteMinimumMark(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/minimum-marks/marks/${id}`);
  }

  async deleteAllMinimumMarks(setId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/minimum-marks/sets/${setId}/marks`);
  }

  async uploadMinimumMarksSetDocument(
    setId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<any> {
    this.ensureAuthenticated();
    const form = new FormData();
    form.append('document', file);
    const response = await this.api.post(
      `/minimum-marks/sets/${setId}/document`,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt: any) => {
          if (onProgress && evt.total) {
            onProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      },
    );
    return response.data;
  }

  async deleteMinimumMarksSetDocument(setId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/minimum-marks/sets/${setId}/document`);
  }

  // ================================
  // 🏆 RANKINGS NACIONALES
  // ================================

  async getRankings(params?: { year?: number; gender?: string; category?: string; status?: string }): Promise<any[]> {
    this.ensureAuthenticated();
    const q = new URLSearchParams();
    if (params?.year) q.set('year', String(params.year));
    if (params?.gender) q.set('gender', params.gender);
    if (params?.category) q.set('category', params.category);
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    const response = await this.api.get(`/rankings${qs ? '?' + qs : ''}`);
    return response.data;
  }

  async getRankingStats(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/rankings/stats');
    return response.data;
  }

  async createRanking(data: { year: number; gender: string; category: string; label?: string; notes?: string }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/rankings', data);
    return response.data;
  }

  async getRanking(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/rankings/${id}`);
    return response.data;
  }

  async updateRanking(id: string, data: { label?: string; notes?: string }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/rankings/${id}`, data);
    return response.data;
  }

  async deleteRanking(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/rankings/${id}`);
  }

  async generateRanking(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/generate`, {}, { timeout: 120000 });
    return response.data;
  }

  async previewRanking(id: string, by?: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/preview`, { by });
    return response.data;
  }

  async approveRanking(id: string, by?: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/approve`, { by });
    return response.data;
  }

  async rejectRanking(id: string, by?: string, reason?: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/reject`, { by, reason });
    return response.data;
  }

  async archiveRanking(id: string, by?: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/archive`, { by });
    return response.data;
  }

  async addRankingEntry(rankingId: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${rankingId}/entries`, data);
    return response.data;
  }

  async updateRankingEntry(rankingId: string, entryId: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/rankings/${rankingId}/entries/${entryId}`, data);
    return response.data;
  }

  async deleteRankingEntry(rankingId: string, entryId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/rankings/${rankingId}/entries/${entryId}`);
  }

  async setupRankingYear(year: number, publish = true): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/rankings/setup-year', { year, publish });
    return response.data;
  }

  async refreshRankingYear(year?: number): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/rankings/refresh-year', year ? { year } : {});
    return response.data;
  }

  async updateRankingConfig(id: string, data: { autoRefresh: boolean }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.patch(`/rankings/${id}/config`, data);
    return response.data;
  }

  async refreshRanking(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${id}/refresh`, {});
    return response.data;
  }

  async getRankingDisciplineConfigs(rankingId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/rankings/${rankingId}/discipline-configs`);
    return response.data;
  }

  async upsertRankingDisciplineConfig(
    rankingId: string,
    data: { discipline: string; sortOrder?: 'asc' | 'desc' | 'auto'; topN?: number | null; displayOrder?: number | null },
  ): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/rankings/${rankingId}/discipline-configs`, data);
    return response.data;
  }

  async updateRankingDisciplineConfig(
    rankingId: string,
    configId: string,
    data: { sortOrder?: 'asc' | 'desc' | 'auto'; topN?: number | null; displayOrder?: number | null },
  ): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/rankings/${rankingId}/discipline-configs/${configId}`, data);
    return response.data;
  }

  async deleteRankingDisciplineConfig(rankingId: string, configId: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/rankings/${rankingId}/discipline-configs/${configId}`);
  }

  async importRankingsExcel(file: File, year: number, replaceExisting: boolean): Promise<any> {
    this.ensureAuthenticated();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', String(year));
    formData.append('replaceExisting', replaceExisting ? 'true' : 'false');
    const response = await this.api.post('/rankings/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  }

  async downloadNationalRecordsPdf(): Promise<{ blob: Blob; fileName?: string }> {
    const response = await this.api.get('/national-records/pdf', {
      responseType: 'blob',
    });

    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
    const fileName = this.parseFileNameFromDisposition(disposition) || 'records-nacionales.pdf';

    return {
      blob: response.data,
      fileName,
    };
  }

  /**
   * 🔓 PÚBLICA - Descargar PDF de resultados de un evento
   */
  async downloadEventResultsPdf(
    eventId: string,
    options?: { resultsJornadaId?: string; pointsJornadaId?: string; pointsCumulative?: boolean; showScoreboard?: boolean; scoreBy?: string; showMedalTable?: boolean },
  ): Promise<{ blob: Blob; fileName?: string }> {
    const params: Record<string, string> = {};
    if (options?.resultsJornadaId) params['resultsJornadaId'] = options.resultsJornadaId;
    if (options?.pointsJornadaId) params['pointsJornadaId'] = options.pointsJornadaId;
    if (options?.pointsCumulative) params['pointsCumulative'] = 'true';
    if (options?.showScoreboard) params['showScoreboard'] = 'true';
    if (options?.scoreBy) params['scoreBy'] = options.scoreBy;
    if (options?.showMedalTable) params['showMedalTable'] = 'true';

    const response = await this.api.get(`/public/events/${eventId}/results/pdf`, {
      responseType: 'blob',
      timeout: 120000,
      params,
    });

    const disposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
    const fileName = this.parseFileNameFromDisposition(disposition) || `resultados-evento-${eventId}.pdf`;

    return {
      blob: response.data,
      fileName,
    };
  }

  private parseFileNameFromDisposition(disposition?: string): string | null {
    if (!disposition) {
      return null;
    }

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch (error) {
        console.warn('No se pudo decodificar el nombre de archivo UTF-8:', error);
      }
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch && asciiMatch[1]) {
      return asciiMatch[1];
    }

    return null;
  }
  // ================================
  // 🏅 PRUEBAS COMBINADAS
  // ================================

  async getCombinedEvents(): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/combined-events');
    return response.data;
  }

  async createCombinedEvent(data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/combined-events', data);
    return response.data;
  }

  async updateCombinedEvent(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/combined-events/${id}`, data);
    return response.data;
  }

  async deleteCombinedEvent(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/combined-events/${id}`);
  }

  async getCombinedEventTestsFilter(combinedEventId: string, categoryId: string, genderId: string): Promise<any[]> {
    this.ensureAuthenticated();
    const response = await this.api.get('/combined-event-test-view/filter', {
      params: { id_combined_event: combinedEventId, id_category: categoryId, id_gender: genderId },
    });
    return response.data;
  }

  async addCombinedEventTest(data: { id_combined_event: string; id_category: string; id_gender: string; id_test: string }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/combined-event-tests', data);
    return response.data;
  }

  async deleteCombinedEventTest(id: string): Promise<void> {
    this.ensureAuthenticated();
    await this.api.delete(`/combined-event-tests/${id}`);
  }

  /**
   * Renombra TODAS las series de todas las pruebas de un evento
   * PATCH /series/event/:eventId/rename-all
   */
  async renameAllEventSeries(eventId: string, name: string = 'Final'): Promise<{ message: string; updated: number; name: string }> {
    this.ensureAuthenticated();
    const response = await this.api.patch(`/series/event/${eventId}/rename-all`, { name });
    return response.data;
  }

  // ================================
  // 📥 IMPORTACIÓN DESDE SISTEMA MAESTRO
  // ================================

  /**
   * Listar eventos del sistema maestro (fdpa_integral)
   * GET /master-import/events
   */
  async getMasterImportEvents(params?: { year?: number; q?: string; limit?: number }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get('/master-import/events', { params });
    return response.data;
  }

  /**
   * Vista previa de un evento antes de importar
   * GET /master-import/events/:extId/preview
   */
  async getMasterImportPreview(extId: number): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/master-import/events/${extId}/preview`);
    return response.data;
  }

  /**
   * Confirmar importación de un evento del sistema maestro
   * POST /master-import/events/:extId/import
   */
  async importMasterEvent(extId: number, body: { confirmed: boolean; stadiumId: string; forceReimport?: boolean }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/master-import/events/${extId}/import`, body);
    return response.data;
  }

  /**
   * Listar eventos del sistema legado (fdpa_data_master_dev)
   * GET /legacy-import/events
   */
  async getLegacyEvents(params?: { year?: number; q?: string; limit?: number; status?: number }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get('/legacy-import/events', { params });
    return response.data;
  }

  /**
   * Vista previa de un evento del sistema legado
   * GET /legacy-import/events/:extId/preview
   */
  async getLegacyEventPreview(extId: number): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/legacy-import/events/${extId}/preview`);
    return response.data;
  }

  /**
   * Confirmar importación de un evento del sistema legado
   * POST /legacy-import/events/:extId/import
   */
  async importLegacyEvent(extId: number, body: { confirmed: boolean; stadiumId?: string | null }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/legacy-import/events/${extId}/import`, body);
    return response.data;
  }

  /**
   * Recalcular puntajes de eventos combinados (decatlón, heptatlón, etc.)
   * POST /lif-data/recompute-combined-points
   */
  async recomputeCombinedPoints(eventId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/lif-data/recompute-combined-points', { eventId });
    return response.data;
  }

  // ================================
  // 🌐 EVENTOS EXTERNOS DE RANKING
  // ================================

  async getExternalRankingEvents(year?: number): Promise<any> {
    this.ensureAuthenticated();
    const params = year ? { year } : undefined;
    const response = await this.api.get('/external-ranking-events', { params });
    return response.data;
  }

  async getExternalRankingEvent(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/external-ranking-events/${id}`);
    return response.data;
  }

  async createExternalRankingEvent(data: { name: string; startDate: string; venue?: string; city?: string; country?: string; endDate?: string; year?: number; notes?: string }): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post('/external-ranking-events', data);
    return response.data;
  }

  async updateExternalRankingEvent(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/external-ranking-events/${id}`, data);
    return response.data;
  }

  async deleteExternalRankingEvent(id: string, deleteEntries = false): Promise<any> {
    this.ensureAuthenticated();
    const url = deleteEntries
      ? `/external-ranking-events/${id}?deleteEntries=true`
      : `/external-ranking-events/${id}`;
    const response = await this.api.delete(url);
    return response.data;
  }

  async getExternalRankingsByYear(year: number): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/external-ranking-events/rankings?year=${year}`);
    return response.data;
  }

  async getExternalRankingEventEntries(id: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.get(`/external-ranking-events/${id}/entries`);
    return response.data;
  }

  async addExternalRankingEventEntry(id: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/external-ranking-events/${id}/entries`, data);
    return response.data;
  }

  async addExternalRankingEventEntriesBatch(id: string, entries: any[]): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.post(`/external-ranking-events/${id}/entries/batch`, { entries });
    return response.data;
  }

  async updateExternalRankingEventEntry(id: string, entryId: string, data: any): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.put(`/external-ranking-events/${id}/entries/${entryId}`, data);
    return response.data;
  }

  async deleteExternalRankingEventEntry(id: string, entryId: string): Promise<any> {
    this.ensureAuthenticated();
    const response = await this.api.delete(`/external-ranking-events/${id}/entries/${entryId}`);
    return response.data;
  }

}

// Instancia singleton
export const apiService = new ApiService();
export default apiService;
